import cls from 'classnames';
import { useRouter } from 'next/router';
import { FC } from 'react';
import { useSelector } from 'react-redux';

import CustomLink from '@/components/custom-link';
import Image from '@/components/image';
import EventNavigation from '@/components/navigation/components/event-navigation';
import { selectGlobalPageTheme } from '@/store/slices/global';
import { selectIsUserLoggedIn } from '@/store/slices/user';
import { NavigationEntry, ThemeSettings } from '@/types/views/generic';

import Navigation from '../navigation';
import NavbarEnergyLogo from './components/navbar-energy-logo';
import ThemeLogo from './components/theme-logo';
import UserMenu from './components/user-menu';
import styles from './styles';

interface EventNavbarProps {
  navigation: NavigationEntry;
  isNavOpen: boolean;
  className?: string;
  onNavToggle: () => void;
  themeSettings?: ThemeSettings;
}

const EventNavbar: FC<EventNavbarProps> = ({
  navigation,
  isNavOpen,
  className = '',
  onNavToggle,
  themeSettings,
}) => {
  const { asPath } = useRouter();
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);

  const pageTheme = useSelector(selectGlobalPageTheme);
  const isLiveSessionEvent = pageTheme === 'els';

  return (
    <div id="navbar" className={cls(styles.wrapper(isNavOpen), className)}>
      <div className={styles.contentContainer}>
        <CustomLink
          href={`/${themeSettings?.urlSlug || ''}`}
          className={styles.energyLogoWrapper(isLiveSessionEvent)}
        >
          {themeSettings && <ThemeLogo themeSettings={themeSettings} />}
        </CustomLink>

        {themeSettings && (
          <NavbarEnergyLogo
            wrapperClassName={cls(
              'flex xl:hidden',
              isLiveSessionEvent && 'tels:ml-28',
            )}
            dividerClassName="tesn:bg-white tels:bg-white teds:bg-white teair:bg-red-light-darker"
            themeSettings={themeSettings}
          />
        )}

        {themeSettings?.eventNav && (
          <EventNavigation
            navItems={themeSettings.eventNav}
            currentAsPath={asPath}
            className="hidden xl:block xl:tels:pl-40 xl:teair:pl-[12rem] xl:tesn:pl-[12rem]"
          />
        )}

        <div className={styles.iconsWrapper}>
          {themeSettings?.locationTimeImage && (
            <div className="items-center hidden xl:flex">
              <Image
                src={themeSettings.locationTimeImage.src}
                useIntrinsicSize
                width={themeSettings.locationTimeImage.width}
                height={themeSettings.locationTimeImage.height}
                alt={`${themeSettings.title} - Location`}
              />
            </div>
          )}

          <UserMenu
            isUserLoggedIn={isUserLoggedIn}
            isNavOpen={isNavOpen}
            onNavToggle={onNavToggle}
          />

          {themeSettings && (
            <NavbarEnergyLogo
              wrapperClassName="hidden xl:flex"
              dividerClassName="tesn:bg-red-light-darker teds:bg-white tels:bg-red-light-darker teair:bg-red-light-darker"
              themeSettings={themeSettings}
            />
          )}
        </div>
      </div>

      {isNavOpen && (
        <Navigation
          className={styles.navigation}
          navigation={navigation}
          onNavItemClick={onNavToggle}
          isEventTheme
          isUserLoggedIn={isUserLoggedIn}
          navigationLowerImage={themeSettings?.locationTimeImage}
        />
      )}
    </div>
  );
};

export default EventNavbar;
